import { Language, ApplicationType } from '../types/translation-types';
import {
  CreateLanguageRequest,
  UpdateLanguageRequest,
  SetApplicationLanguagesRequest,
  LanguageListResponse,
  DeleteLanguageResponse,
  ApplicationLanguagesResponse,
  SetApplicationLanguagesResponse,
  LanguageBatchReorderRequest,
  LanguageBatchReorderResponse,
} from '../types/language-management-types';
import { LanguageManagementApi } from './language-management-api';

/**
 * Language Management Repository
 * Provides a clean interface for language management operations
 */
export class LanguageManagementRepository {
  constructor(private readonly api: LanguageManagementApi) {}

  // ============================================================================
  // Global Languages CRUD
  // ============================================================================

  /**
   * Get all languages
   */
  async getLanguages(): Promise<LanguageListResponse> {
    return this.api.getLanguages();
  }

  /**
   * Get a single language by ID
   */
  async getLanguageById(languageId: number): Promise<Language> {
    return this.api.getLanguageById(languageId);
  }

  /**
   * Create a new language
   */
  async createLanguage(data: CreateLanguageRequest): Promise<Language> {
    return this.api.createLanguage(data);
  }

  /**
   * Update an existing language
   */
  async updateLanguage(languageId: number, data: UpdateLanguageRequest): Promise<Language> {
    return this.api.updateLanguage(languageId, data);
  }

  /**
   * Delete a language
   */
  async deleteLanguage(languageId: number): Promise<DeleteLanguageResponse> {
    return this.api.deleteLanguage(languageId);
  }

  /**
   * Batch update language sort orders
   */
  async batchReorderLanguages(request: LanguageBatchReorderRequest): Promise<LanguageBatchReorderResponse> {
    return this.api.batchReorderLanguages(request);
  }

  // ============================================================================
  // Application Languages
  // ============================================================================

  /**
   * Get languages configured for an application
   */
  async getApplicationLanguages(applicationType: ApplicationType): Promise<ApplicationLanguagesResponse> {
    return this.api.getApplicationLanguages(applicationType);
  }

  /**
   * Set languages for an application
   */
  async setApplicationLanguages(
    applicationType: ApplicationType,
    request: SetApplicationLanguagesRequest
  ): Promise<SetApplicationLanguagesResponse> {
    return this.api.setApplicationLanguages(applicationType, request);
  }
}
